import cn from "classnames";
import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  FocusEvent,
  ChangeEvent,
  useCallback,
  MouseEvent,
} from "react";
import {
  Close as CloseIcon,
  Calendar as CalendarIcon,
} from "@rshbintech.dboul/icons";
import { usePrevious } from "../common/utils/use-previous";
import { MaskInput } from "../mask-input";
import { Flyout } from "../flyout";
import { mergeRefs } from "../common/utils/merge-refs";
import { Position } from "../flyout/types";
import { useLatestRef } from "../common/hooks/use-latest-ref";
import { Calendar } from "./calendar";
import {
  maskDate,
  isRangeMode,
  isEmptyDate,
  isFilledDate,
  getInputValue,
  formatDateToISO,
  formatDateToLocale,
  getCorrectDate,
  getInputValueByMode,
  isFilledDateRu,
  getEmptyDate,
  isEqualDate,
} from "./utils";
import {
  EMPTY_DATE,
  RANGE_DATE_INPUT_MASK,
  RANGE_DATE_INPUT_PLACEHOLDER,
  RANGE_DELIMETER,
  SINGLE_DATE_INPUT_MASK,
  SINGLE_DATE_INPUT_PLACEHOLDER,
} from "./constants";
import { DatePickerProps, DateValue } from "./types";
import { useFlyoutPositions } from "./user-flyout-positions";
import styles from "./styles.module.scss";

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      className,
      value = "",
      minDate = "",
      maxDate = "",
      disabled = false,
      dataTestId = "datepicker",
      onFocus,
      onChange,
      onBlur,
      ...restProps
    },
    forwardedRef
  ) => {
    const { currentPosition, flyoutPositions, setFlyoutPosition, isMobile } =
      useFlyoutPositions();

    const datepickerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const mergedInputRef = mergeRefs(forwardedRef, inputRef);

    const prevValue = usePrevious(value);
    const [inputValue, setInputValue] = useState("");
    const [focused, setFocused] = useState(false);
    const [openedCalendar, setOpenedCalendar] = useState(false);

    const dayRefs = useRef<HTMLButtonElement[]>([]);

    const isRange = isRangeMode(value);

    const handlePositionChange = useCallback(
      (position: Position) => {
        if (flyoutPositions.includes(position)) {
          setFlyoutPosition(position);
        }
      },
      [flyoutPositions, setFlyoutPosition]
    );

    useEffect(() => {
      if (!openedCalendar) {
        // INFO: Убираем фокус с поля ввода после закрытия пикера, чтобы была возможность снова открыть пикер при клике по инпуту
        inputRef.current?.blur();
      }
    }, [openedCalendar]);

    const onChangeRef = useLatestRef(onChange);
    const handleChange = useCallback(
      (date: DateValue) => {
        const isoValue = formatDateToISO(date);

        if (!isEqualDate(date, value)) {
          onChangeRef.current?.(isoValue);
        }
      },
      [onChangeRef, value]
    );

    const checkAndUpdateDate = useCallback(
      (newInputValue: string, newDateValue: DateValue) => {
        if (isFilledDateRu(newDateValue, true)) {
          const correctDate = getCorrectDate(
            minDate,
            maxDate,
            getInputValueByMode(newInputValue, isRange)
          );

          const inputValueFromDate = getInputValue(correctDate);

          setInputValue(inputValueFromDate);
          handleChange(correctDate);
        } else {
          setInputValue(newInputValue);
          handleChange(newDateValue);
        }
      },
      [handleChange, isRange, maxDate, minDate]
    );

    useEffect(() => {
      // INFO: Проверяем заполнение начала диапазона при инициализации компонента и при обновлении стартовой даты
      const needCheckStartDate = !prevValue || isEmptyDate(prevValue);
      const isFilledDateValue = isFilledDate(value, {
        isPartial: needCheckStartDate,
      });

      const needResetInputValue =
        isFilledDateRu(getInputValueByMode(inputValue, isRange)) &&
        isEmptyDate(value);

      if (isFilledDateValue || needResetInputValue) {
        const localValue = formatDateToLocale(value);
        const updatedInputValue = getInputValue(localValue);

        if (updatedInputValue !== inputValue) {
          checkAndUpdateDate(updatedInputValue, localValue);
        }
      }
    }, [checkAndUpdateDate, inputValue, isRange, prevValue, value]);

    const handleCalendarOpen = () => {
      setOpenedCalendar(true);
    };

    const handleCalendarClose = () => {
      setOpenedCalendar(false);
    };

    useEffect(() => {
      if (openedCalendar && disabled) {
        handleCalendarClose();
      }
    }, [disabled, openedCalendar]);

    const handleInputFocus = (event: FocusEvent<HTMLInputElement>) => {
      if (!openedCalendar) {
        handleCalendarOpen();
      }

      setFocused(true);
      onFocus?.(event);
    };

    const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(event);
    };

    const handleCalendarSelect = (value: DateValue) => {
      if (isFilledDateRu(value)) {
        handleCalendarClose();
      }

      // INFO: Необходимо для корректного смещения каретки ввода при выборе промежутка дат,
      // поскольку мы предотвращаем всплытие нажатия клавиши мыши на элемент календаря
      if (isRange && focused) {
        requestAnimationFrame(() => {
          inputRef.current?.setSelectionRange(
            getInputValue(value).length,
            getInputValue(value).length
          );
        });
      }

      if (inputRef.current && isFilledDateRu(value, true) && isRange) {
        // требуется для случая, когда пользователь частично заполняет начальную дату в поле ввода, а затем выбирает дату из календаря
        // text-mask-core некорректно обрабатывает такой случай и маска сбивается
        // нам нужно заменить введённое пользователем значение на то, которое он выбрал в панели календаря
        inputRef.current.value = getInputValue(value);
      }

      checkAndUpdateDate(getInputValue(value), value);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      let newDateValue: DateValue;

      const { value: newInputValue } = event.target;

      if (isRange) {
        const [startDate, endDate = EMPTY_DATE] =
          newInputValue.split(RANGE_DELIMETER);

        const isFilledStartDate = isFilledDateRu(startDate);

        if (isFilledStartDate) {
          // изменение конечной даты
          const maskedEndDate = maskDate(endDate);
          const isFilledEndDate = isFilledDateRu(maskedEndDate);
          const newEndDate = isFilledEndDate ? maskedEndDate : EMPTY_DATE;

          newDateValue = [startDate, newEndDate];
        } else {
          // изменение начальной даты
          const maskedStartDate = maskDate(startDate);
          const newStartDate = isFilledStartDate ? maskedStartDate : EMPTY_DATE;

          newDateValue = [newStartDate, EMPTY_DATE];
        }
      } else {
        const maskedDate = maskDate(newInputValue);
        const newDate = isFilledDateRu(maskedDate) ? maskedDate : EMPTY_DATE;

        newDateValue = newDate;
      }

      checkAndUpdateDate(newInputValue, newDateValue);
    };

    const handleInputClear = () => {
      handleChange(getEmptyDate(isRange));
      setInputValue(EMPTY_DATE);
    };

    const handleCalendarWrapperMouseDown = (
      event: MouseEvent<HTMLDivElement>
    ) => {
      /*
        INFO: Предотвращаем потерю фокуса инпутом при кликах внутри календаря
        необходимо для исключения смещения лейбла при выборе даты. Без этой строчки происходят следующие действия:
        1) пользователь кликает в инпут -> лейбл смещается вверх-влево, открывается пикер
        2) пользователь выбирает дату -> происходит onBlur на инпуте -> лейбл возвращается в центр инпута
        3) срабатывает onClick на выбранной пользователем кнопке -> выбирается дата, форматируется и устанавливается значение в инпут -> лейбл смещается вверх-влево
      */
      event.preventDefault();
    };

    const handleCalendarIconClick = () => {
      if (openedCalendar) {
        handleCalendarClose();
      } else {
        handleCalendarOpen();
      }
    };

    const rightIcon =
      !disabled && inputValue
        ? {
            icon: <CloseIcon size="medium" />, // TODO: Необходимо уточнить нужно ли скрывать иконку крестика после потери фокуса с поля ввода, по образу и подобию реализации в Input.
            onIconPress: handleInputClear,
          }
        : {
            icon: <CalendarIcon size="medium" />,
            onIconPress: handleCalendarIconClick,
          };

    const placeholder = focused
      ? isRange
        ? RANGE_DATE_INPUT_PLACEHOLDER
        : SINGLE_DATE_INPUT_PLACEHOLDER
      : undefined;

    return (
      <div
        ref={datepickerRef}
        className={cn(styles.datepicker, className)}
        data-test-id={dataTestId}
      >
        <MaskInput
          placeholder={placeholder}
          mask={isRange ? RANGE_DATE_INPUT_MASK : SINGLE_DATE_INPUT_MASK}
          showMask
          keepCharPositions
          ref={mergedInputRef}
          value={inputValue}
          disabled={disabled}
          autoComplete="off"
          rightIcon={rightIcon}
          className={styles.input}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          dataTestId={`${dataTestId}-input`}
          {...restProps}
        />
        {openedCalendar && (
          <Flyout
            parentRef={datepickerRef}
            className={cn(styles.flyout, styles[currentPosition])}
            positionsOrder={flyoutPositions}
            onChangePosition={handlePositionChange}
            shouldUseParentWidth={isMobile}
          >
            <div onMouseDown={handleCalendarWrapperMouseDown}>
              <Calendar
                value={value}
                minValue={minDate}
                maxValue={maxDate}
                datepickerRef={datepickerRef}
                dayRefs={dayRefs}
                onSelectDate={handleCalendarSelect}
                onOutsideClick={handleCalendarClose}
                dataTestId={`${dataTestId}-calendar`}
              />
            </div>
          </Flyout>
        )}
      </div>
    );
  }
);

DatePicker.displayName = "DatePicker";
